import React, { useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import CircleReveal from './experiments/circle-reveal/CircleReveal';
import VelocityCards from './experiments/velocity-cards/VelocityCards';
import RedType from './experiments/red-type/RedType';
import GenGrid from './experiments/gen-grid/GenGrid';
import BlindReveal from './experiments/blind-reveal/BlindReveal';
import LowPolyOrbit from './experiments/low-poly-orbit/LowPolyOrbit';
import TextRepel from './experiments/text-repel/TextRepel';
import LiquidType from './experiments/liquid-type/LiquidType';
import InkDrop from './experiments/ink-drop/InkDrop';
import NewsTunnel from './experiments/news-tunnel/NewsTunnel';
import VoiceGravity from './experiments/voice-gravity/VoiceGravity';
import MelodySelect from './experiments/melody-select/MelodySelect';
import AsciiLens from './experiments/ascii-lens/AsciiLens';
import BranchVeins from './experiments/branch-veins/BranchVeins';
import DotBreath from './experiments/dot-breath/DotBreath';
import InkSound from './experiments/ink-sound/InkSound';
import ScrollCinema from './experiments/scroll-cinema/ScrollCinema';
import FossilDig from './experiments/fossil-dig/FossilDig';
import BreathGlass from './experiments/breath-glass/BreathGlass';
import './App.css';

type Category = 'exp' | 'work' | 'archive';

interface Experiment {
  id: string;
  number: string;
  title: string;
  description: string;
  thumbnail: string;
  tags: string[];
  status: 'live' | 'wip' | 'planned';
  category: Category;
  date: string;
  url?: string;
  prompt?: string;
}

const experiments: Experiment[] = [
  {
    id: 'clock',
    number: '01',
    title: 'Digital Clock',
    description: 'Retro Mac Classic style digital clock screensaver with CRT effects, time-based color shifting, and terminal boot animation.',
    thumbnail: '00:00',
    tags: ['screensaver', 'CRT', 'time', 'retro'],
    status: 'live',
    category: 'work',
    date: '2026.03',
    url: 'https://dkdk.me',
    prompt: 'Create a retro Mac Classic style digital clock screensaver with CRT monitor effects, time-based color shifting, and a terminal boot sequence animation.',
  },
  {
    id: 'circle-reveal',
    number: '02',
    title: 'Circle Reveal',
    description: 'SVG clipPath circular mask expanding from center on scroll. Fullpage transitions powered by GSAP ScrollTrigger.',
    thumbnail: '◎',
    tags: ['SVG', 'GSAP', 'scroll', 'clipPath', 'transition'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/circle-reveal',
    prompt: '스크롤을 내릴 때 SVG clipPath의 원형 마스크가 화면 중앙에서 확장되면서 아래 이미지를 드러내는 풀페이지 전환 효과를 GSAP ScrollTrigger와 함께 구현해줘 — 이미지 3장, 각 섹션 height 100vh',
  },
  {
    id: 'velocity-cards',
    number: '03',
    title: 'Velocity Cards',
    description: 'Three.js image cards at varying z-depths. Wheel velocity controls rotation speed and background hue shift — blue at rest, orange at speed.',
    thumbnail: '▲',
    tags: ['Three.js', 'wheel', 'velocity', 'hue', 'vanilla JS'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/velocity-cards',
    prompt: 'Three.js로 3~5장 이미지 카드를 z축 깊이 달리 배치하고, wheel 이벤트의 deltaY를 velocity로 계산해 카드 회전 속도와 배경 hue를 실시간 연동해봐. 느리면 블루, 빠르면 오렌지. GSAP 대신 순수 JS로 먼저 프로토타입해봐.',
  },
  {
    id: 'red-type',
    number: '04',
    title: 'Red Type',
    description: 'Two colors (#F90000 + #14181E), one typeface. GSAP ScrollTrigger text build-up with mouse-reactive gradient shift. Pure CSS + GSAP immersion experiment.',
    thumbnail: 'R',
    tags: ['GSAP', 'typography', 'scroll', 'gradient', 'minimal'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/red-type',
    prompt: '두 색(#F90000 + #14181E)만으로 GSAP ScrollTrigger로 텍스트 글자 단위 빌드업 + 마우스 위치에 따라 배경 그라디언트가 shift되는 원페이지. Three.js 없이 CSS + GSAP만으로 몰입감 실험.',
  },
  {
    id: 'gen-grid',
    number: '05',
    title: 'Generative Grid',
    description: 'CSS Grid 6×6 with random HSL backgrounds and per-corner border-radius. Every refresh produces a unique generative pattern.',
    thumbnail: '▦',
    tags: ['CSS Grid', 'generative', 'random', 'HSL', 'border-radius'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/gen-grid',
    prompt: 'CSS Grid 6×6에 각 셀마다 random()으로 배경색(hsl 범위 지정)과 border-radius를 랜덤 적용한 제너러티브 패턴을 만들어봐. 새로고침할 때마다 달라지는 느낌이 핵심.',
  },
  {
    id: 'blind-reveal',
    number: '06',
    title: 'Blind Reveal',
    description: 'SVG <rect> vertical blinds masking fullscreen images. GSAP ScrollTrigger scrub opens each blind with wave-like stagger to reveal the next photo.',
    thumbnail: '|||',
    tags: ['SVG', 'GSAP', 'scroll', 'mask', 'stagger', 'transition'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/blind-reveal',
    prompt: '풀스크린 이미지 3장을 준비하고, SVG <rect> 10개로 수직 블라인드 마스크를 만들어봐. GSAP ScrollTrigger의 scrub 모드로 스크롤하면 블라인드가 하나씩 열리면서 다음 이미지가 드러나는 트랜지션을 구현해. 각 블라인드의 열리는 타이밍에 stagger를 줘서 물결처럼 퍼지게 만들어봐.',
  },
  {
    id: 'low-poly-orbit',
    number: '07',
    title: 'Low Poly Orbit',
    description: 'Three.js low-poly geometries floating in 3D space. GSAP ScrollTrigger drives a smooth camera orbit, with background color shifting across scroll sections.',
    thumbnail: '⬡',
    tags: ['Three.js', 'GSAP', 'scroll', '3D', 'low-poly', 'camera'],
    status: 'live',
    category: 'exp',
    date: '2026.03',
    url: '/low-poly-orbit',
    prompt: 'Three.js로 로우폴리 큐브 몇 개가 떠 있는 씬을 만들고, 스크롤에 따라 카메라가 오브젝트 주위를 부드럽게 공전하도록 GSAP ScrollTrigger 연결. 배경색도 스크롤 구간별로 그라데이션 전환. 개인 사이트 히어로 섹션 프로토타입으로 실험.',
  },
  {
    id: 'text-repel',
    number: '08',
    title: 'Gravity Type',
    description: 'Device gyroscope controls text gravity. Tilt to pour letters, level to restore. Wall collisions spawn burst particles. Pretext-powered character physics at 60fps.',
    thumbnail: 'G',
    tags: ['Pretext', 'gyroscope', 'physics', 'canvas', 'gravity'],
    status: 'live',
    category: 'exp',
    date: '2026.04',
    url: '/text-repel',
    prompt: '기기의 물리적인 회전에 따라 텍스트가 해당 방향으로 쏟아지는 페이지. 중심이 잘 맞을수록 텍스트는 원래대로 돌아옴. 벽에 부딪힐 때 작은 텍스트가 파티클처럼 터지는 연출.',
  },
  {
    id: 'liquid-type',
    number: '09',
    title: 'Liquid Type',
    description: 'Pretext-powered kinetic typography. Scroll drives dynamic viewport width changes, causing real-time text reflow at 60fps. Two colors (#14181E + #00FF99), one typeface.',
    thumbnail: '~',
    tags: ['Pretext', 'scroll', 'typography', 'reflow', 'kinetic'],
    status: 'live',
    category: 'exp',
    date: '2026.04',
    url: '/liquid-type',
    prompt: 'Pretext로 스크롤 위치에 따라 텍스트가 유체처럼 리플로우되는 원페이지. 뷰포트 너비를 스크롤 진행률에 따라 동적으로 변화시켜 텍스트 줄바꿈이 실시간으로 재배치되는 키네틱 타이포그래피 실험. 배경은 #14181E, 텍스트는 #00FF99.',
  },
  {
    id: 'news-tunnel',
    number: '11',
    title: 'News Tunnel',
    description: 'Pretext-powered newspaper grid. Click any paragraph to enter a rectangular tunnel of text — the article continues as characters wrapping every wall, receding to a vanishing point.',
    thumbnail: '▣',
    tags: ['Pretext', 'canvas', 'typography', 'tunnel', 'newspaper'],
    status: 'live',
    category: 'exp',
    date: '2026.04',
    url: '/news-tunnel',
    prompt: 'Pretext를 활용. 화면 전체는 텍스트로 가득 찬 신문 형식 그리드. 단락을 클릭하면 텍스트로 이뤄진 사각형 터널이 나타나면서 화면은 해당 터널로 줌인. 신문의 내용이 직각으로 이어지는 사각형 터널.',
  },
  {
    id: 'ink-drop',
    number: '10',
    title: 'Ink Drop',
    description: 'Cursor drops ink on paper canvas. Slow strokes bleed like sumi-e, fast strokes splash. Drops merge and spread when close. Ink dries over time, leaving traces.',
    thumbnail: '墨',
    tags: ['canvas', 'physics', 'ink', 'interaction', 'generative'],
    status: 'live',
    category: 'exp',
    date: '2026.04',
    url: '/ink-drop',
    prompt: '마우스 커서가 잉크 방울을 떨어뜨리는 캔버스 — 마우스를 움직이면 커서 위치에서 잉크 방울이 떨어지고, 방울끼리 만나면 수묵화처럼 번지면서 섞여. 마우스 속도가 빠르면 잉크가 튀는 스플래시 효과, 느리면 천천히 퍼지는 번짐 효과. 일정 시간이 지나면 잉크가 서서히 마르면서 투명해지고, 페이지 위에 사용자의 움직임 흔적이 수묵 드로잉처럼 남아.',
  },
  {
    id: 'voice-gravity',
    number: '12',
    title: 'Voice Gravity',
    description: 'Microphone volume becomes page gravity. Web Audio API measures ambient dB in real-time and maps it to gravitational acceleration. Whisper to lift text upward, shout to crash it into the floor. The page sleeps until you make a sound.',
    thumbnail: '♪',
    tags: ['Web Audio', 'microphone', 'physics', 'canvas', 'gravity', 'interaction'],
    status: 'live',
    category: 'exp',
    date: '2026.05',
    url: '/voice-gravity',
    prompt: '마이크 볼륨이 페이지의 중력이 되는 사이트 - web audio api로 주변 소음 dB를 실시간 측정해 페이지 요소의 중력 가속도로 매핑. 속삭이면 텍스트, 이미지가 둥둥 떠오르고, 박수치거나 큰 소리가 나면 일제히 추락해 바닥에 쌓인다. 페이지를 ‘깨우려면’ 사용자가 목소리를 내야한다.',
  },
  {
    id: 'melody-select',
    number: '13',
    title: 'Melody Select',
    description: 'Drag-selecting body text plays a melody. Each character\'s charCode maps to a pentatonic note, drag speed becomes tempo, selection length sets sustain. Double-click a word for a chorus tail. The familiar copy gesture, bent into a composition interface.',
    thumbnail: '♬',
    tags: ['Web Audio', 'selection', 'pentatonic', 'composition', 'interaction'],
    status: 'live',
    category: 'exp',
    date: '2026.05',
    url: '/melody-select',
    prompt: '본문 텍스트를 드래그 선택하면 멜로디가 연주되는 페이지 - 사용자가 단어, 문장을 selection으로 긁으면, 각 글자의 charCode를 펜타토닉 스케일에 매핑해 노트로 발음. 드래그 속도는 템포, 선택 길이는 sustain. 더블클릭한 단어는 코러스로 잔향이 남는다. 복사라는 익숙한 동작을 작곡 인터페이스로 비트는 게 포인트',
  },
  {
    id: 'ascii-lens',
    number: '14',
    title: 'ASCII Lens',
    description: 'A 200px lens follows the cursor, converting only the pixels beneath it into live ASCII. Move faster and the cells shrink from 8px to 4px (EMA-smoothed velocity). Click to freeze the whole screen into ASCII; click again to release. Pure Canvas 2D, monochrome.',
    thumbnail: '@',
    tags: ['Canvas', 'ASCII', 'cursor', 'pixel', 'monochrome'],
    status: 'live',
    category: 'exp',
    date: '2026.06',
    url: '/ascii-lens',
    prompt: 'HTML/JS로 단일 페이지 인터랙티브 데모를 만들어줘. 화면 전체에 부드러운 라디얼 그라데이션 배경을 깔고, 그 위에 큰 영문 시 한 편을 어두운 글자색으로 배치한다. 커서를 따라 반경 200px의 원형 영역만 실시간 ASCII로 변환된다(영역 밖은 원본 그대로). 두 번째 Canvas를 오버레이로 깔고 그 안에만 ASCII를 그린다. ASCII 변환은 해당 영역을 getImageData로 읽어 픽셀 밝기 → 문자(" .:;+*#@")로 매핑, 기본 8px 셀로 monospace 폰트로 그린다. 마우스가 빨라질수록 셀 크기가 4px까지 작아진다(속도는 px/ms 측정 후 EMA α=0.2로 평활화). 클릭하면 전체 화면이 ASCII로 굳고, 다시 클릭하면 풀린다. 토글은 부드럽게 보간. 색은 단색 흑백 무채색만. 외부 라이브러리 금지(Three.js·GSAP·p5 모두 X) — 순수 Canvas 2D + requestAnimationFrame + JS만.',
  },
  {
    id: 'branch-veins',
    number: '15',
    title: 'Branch Veins',
    description: 'Every 5 seconds a new branch sprouts from a random screen edge and grows inward by L-system rules — extending and forking each tick. Branches blur the diary text beneath them (max 3px / opacity 0.2). Click a branch and GSAP shrinks it to scale 0 over 0.6s; the covered words restore.',
    thumbnail: 'Y',
    tags: ['SVG', 'L-system', 'generative', 'GSAP', 'typography'],
    status: 'live',
    category: 'exp',
    date: '2026.06',
    url: '/branch-veins',
    prompt: 'HTML/JS로 1페이지 인터랙티브 데모를 만들어줘. 페이지 중앙에 짧은 글(임의의 일기 한 단락)을 serif 시스템 폰트로 표시한다. 페이지 로드 시점부터 경과한 시간을 측정한다. 5초마다 화면 가장자리 임의 위치에서 새로운 가지가 돋아나고, 매 프레임마다 기존 가지는 L-system 규칙으로 길이•분기를 늘려간다. 가지는 SVG path로 그리되, 텍스트 영역과 겹치면 해당 글자에 점진적으로 blur(최대 3px)와 opacity 감소(최저 0.2)를 적용해 읽기 어려워지게 한다. 사용자가 가지를 클릭하면 그 가지만 0.6s ease-out으로 Scale 0까지 줄며 사라지고, 가렸던 글자의 blur opacity가 GSAP으로 부드럽게 복구된다. 색은 단일 잉크톤(#1a1a1a) on warm paper(#f3eee6). 외부 라이브러리는 GSAP만 허용, Three.js p5 금지.',
  },
  {
    id: 'dot-breath',
    number: '16',
    title: 'Dot Breath',
    description: 'The Korean character "숨" is sampled into ~1100 white dots on a black canvas. Mic RMS above 0.05 drops the dots from their letter positions with gravity and a two-band sine sway; silence springs them home (tension 0.08 / friction 0.85). Web Audio + pure Canvas 2D.',
    thumbnail: '숨',
    tags: ['Web Audio', 'microphone', 'particles', 'Canvas', 'typography', 'Korean'],
    status: 'live',
    category: 'exp',
    date: '2026.06',
    url: '/dot-breath',
    prompt: 'HTML/JS로 1페이지 인터랙티브 데모를 만들어줘. 시작 버튼을 누르면 getUserMedia로 마이크 입력을 받고 Web Audio AnalyserNode로 RMS 볼륨을 매 프레임 측정한다. 화면 중앙에 큰 한글 타이포(예: "숨")를 Canvas에 점(파티클) 군집으로 렌더링한다. 글자 형태 샘플링으로 800~1500개 점을 분포시켜라. 마이크 볼륨이 임계값(0.05)을 넘으면 그 강도에 비례해 파티클을 글자 위치에서 떨어뜨려라(중력 + Perlin/사인 노이즈 기반 가로 흔들림). 조용해지면 각 파티클이 원래 글자 위치로 스프링 보간(tension 0.08, friction 0.85)되어 돌아온다. 색은 무채색만(검정 배경 + 흰 점). 폰트는 시스템 sans-serif. 외부 라이브러리 금지, 순수 Canvas2D + JS만. 권한 거부 시 빈 화면 대신 "마이크 권한이 필요합니다" 안내 + 재시도 버튼을 보여준다.',
  },
  {
    id: 'ink-sound',
    number: '17',
    title: 'Ink Sound',
    description: 'Pointer leaves a thick black ink trail on white that slowly fades under a per-frame alpha veil. Cursor Y is quantized to a two-octave pentatonic scale driving an OscillatorNode, drag speed maps to gain (silent at rest). Click sends a ripple expanding past the edges while the waveform morphs sine→square→sine over 200ms. Pure Canvas 2D + Web Audio, monochrome.',
    thumbnail: '∿',
    tags: ['Canvas', 'Web Audio', 'pentatonic', 'oscillator', 'ink', 'monochrome'],
    status: 'live',
    category: 'exp',
    date: '2026.06',
    url: '/ink-sound',
    prompt: 'HTML/JS 한 페이지 데모를 만들어줘. 외부 라이브러리 없이 순수 JS/Canvas/Web Audio API만 사용.\n- 흰 배경 전체 뷰포트, 그 위에 전체화면 캔버스를 깐다.\n- 포인터(마우스+터치) 이동 좌표를 매 프레임 캡처해 캔버스에 두꺼운 검은 잉크 트레일을 그린다. 매 프레임 전체에 alpha 0.04 흰색 사각형을 덮어 잔상이 천천히 사라지게.\n- 동시에 커서 Y 좌표를 펜타토닉 음계(C·D·E·G·A 두 옥타브)로 양자화해 OscillatorNode 주파수에 매핑, 속도(px/ms)는 gain(0~0.25)으로. 정지 시 무음.\n- 클릭/탭하면 트레일이 한 번에 원형 파동으로 캔버스 바깥까지 확산되고, 동시에 사인파가 사각파로 200ms 모핑됐다 돌아온다.\n- 흑백 무채색만, 폰트는 시스템 sans-serif, 좌상단에 "draw to play" 한 줄만 작게 표시.\n- 첫 클릭에서 AudioContext.resume()으로 자동재생 정책 우회할 것.',
  },
  {
    id: 'scroll-cinema',
    number: '18',
    title: 'Scroll Cinema',
    description: 'A 4-act cinematic scroll piece with zero JavaScript — every motion is a CSS scroll-driven animation (animation-timeline: scroll()/view(), animation-range, timeline-scope). Centered title explodes into particles, colour floods the screen, a giant circle sweeps across in mix-blend-mode, then everything settles to grey. Only transform/opacity/filter animate; palette is #000/#fff/#d4ff00. Reduced-motion snaps every step.',
    thumbnail: '⌗',
    tags: ['CSS-only', 'scroll-driven', 'animation-timeline', 'mix-blend', 'no-JS'],
    status: 'live',
    category: 'exp',
    date: '2026.07',
    url: '/scroll-cinema',
    prompt: 'HTML/CSS만으로 1페이지 시네마틱 스크롤 경험을 만들어줘. JavaScript는 단 한 줄도 쓰지 말 것.\n- 페이지는 4막 구성: (1) 흑백 텍스트가 화면 정중앙에 떠 있다가, (2) 스크롤하면 텍스트가 폭발하듯 분해되고 배경에 색이 채워지고, (3) 다음 섹션에서는 거대한 circle 도형이 좌→우로 흐르며 mix-blend-mode로 텍스트와 겹쳐지고, (4) 마지막 섹션에서 모든 것이 회색으로 수렴한다.\n- 모든 애니메이션은 animation-timeline: scroll() 또는 view()만 사용. 키프레임은 animation-range로 정밀 제어.\n- 두 개 이상의 요소가 같은 scroll progress를 공유해야 하면 timeline-scope를 활용.\n- transform·opacity·filter만 애니메이트(레이아웃 트리거 금지).\n- 색상은 #000, #fff, #d4ff00 단 3색만. 폰트는 시스템 sans-serif 또는 Inter.\n- @media (prefers-reduced-motion: reduce) 분기에서는 모든 애니메이션을 step-end로 즉시 끝나게.',
  },
  {
    id: 'fossil-dig',
    number: '19',
    title: 'Web Fossil Dig',
    description: 'A cross-section of earth in brown/gray/beige strata. Double-click to carve a circular hole one layer deeper — the *rhythm* of your clicks becomes pickaxe force: fast taps strike hard (big debris + 3–5px screen shake + CRACK!), slow taps brush dust down. Dig past 5 layers and a fossil surfaces (Wi-Fi glyph, pixel dino, floppy, or bone). No audio — the sound of digging is faked purely with motion-blurred debris, shake, and onomatopoeia pop-ups. Pure Canvas 2D + rAF + particles.',
    thumbnail: '☰',
    tags: ['Canvas', 'particles', 'double-click', 'shake', 'playful'],
    status: 'live',
    category: 'exp',
    date: '2026.08',
    url: '/fossil-dig',
    prompt: 'HTML/JS로 1페이지 인터랙티브 데모 "웹 화석 발굴장"을 만들어줘.\n- 화면 전체가 갈색·회색·베이지 3색으로 겹겹이 쌓인 지층. 각 층은 Canvas로 그린 살짝 노이즈 낀 띠 5~7겹.\n- 사용자의 더블클릭 간격(ms)을 매번 측정해 "곡괭이 세기"로 매핑: 짧으면(<250ms) 흙 파편이 크게 튀고 화면이 3~5px shake, 길면(>600ms) 붓질처럼 흙 먼지가 살랑 떨어진다.\n- 더블클릭 좌표를 중심으로 원형 지층이 한 겹씩 깎여 사라지고 아래 층이 드러난다. 5층 이상 파고들면 "화석" 이스터에그 랜덤 등장 — Wi-Fi 심볼 화석, 픽셀 공룡, 옛날 플로피, 이모지 뼈 중 하나.\n- 오디오는 절대 쓰지 말고, 오직 시각으로 \'땅 파는 소리\'가 들리는 듯한 착각을 만든다: 만화적 파편 잔상, shake, 파편에 모션 블러, "CRACK!"·"THUD!" 같은 온오마토피 텍스트가 팝업 후 페이드.\n- 상단에 "🦕 오늘의 발굴 깊이: XX층" 카운터. 톤은 유머·장난기.\n- 외부 라이브러리 금지, 순수 Canvas 2D + requestAnimationFrame + 파티클로만.',
  },
  {
    id: 'breath-glass',
    number: '20',
    title: 'Breath on Glass',
    description: 'Mic RMS past a threshold counts as a breath, and fog seeps from the top third of an old wooden window. The fog spreads by viscosity — a laplacian relaxation over a 96×96 density grid, ragged-edged by value noise — so it creeps instead of scattering. Once it settles thick enough a name is not drawn but WIPED out of it (destination-out) with a condensation ridge, stroke by stroke over 2s. Go quiet and the fog evaporates, taking the name with it; only then can the next breath bring a new one.',
    thumbnail: '▧',
    tags: ['Web Audio', 'microphone', 'Canvas', 'viscosity', 'value-noise', 'monochrome'],
    status: 'live',
    category: 'exp',
    date: '2026.08',
    url: '/breath-glass',
    prompt: 'HTML/JS로 1페이지 인터랙티브 데모를 만들어줘. 스크롤 금지, 한 화면에 고정된 정사각 캔버스 하나만 사용.\n\n장면: 화면 중앙에 오래된 목재 창틀 안 유리창 한 장. 마이크(getUserMedia)로 실시간 입력 음량(RMS)을 받는다. 음량이 특정 임계값을 넘으면 \'숨을 불었다\'로 판정하고, 유리 위쪽 3분의 1부터 점성 있는 김이 서서히 퍼진다. 점성 표현은 노이즈 필드(간단한 value noise) 위에 시간·음량으로 이동하는 가우시안 블롭들을 알파 누적으로 그리기. 김의 확산은 velocity가 아니라 viscosity로 — 즉시 흩어지지 않고 끈적하게 번져 나감.\n\n김이 충분히 서린 영역 위로 손글씨(cursive) 폰트로 이름 한 단어(예: \'지원\', \'엄마\', \'2007\')가 서서히 얇은 선으로 나타난다(마치 손가락으로 쓴 듯 stroke by stroke, 2초에 걸쳐). 이름은 매 세션 랜덤. 사용자가 다시 침묵하면 김이 서서히 걷히고, 이름도 김과 함께 사라진다. 이름이 완전히 사라진 뒤에야 다음 숨결로 새 이름이 나타난다.\n\n색은 무채색+아주 옅은 세피아 톤 2색만. 배경은 어두운 회색, 유리는 반투명 옅은 청회색. 사운드 재생·라이브러리 사용 금지, 순수 JS + Canvas 2D만. 마이크 권한 요청 UI는 첫 진입 시 조용한 안내 문구 한 줄로.',
  },
];

const categoryLabel: Record<Category, string> = {
  exp: 'EXP',
  work: 'WORK',
  archive: 'ARCHIVE',
};

type SortKey = 'newest' | 'oldest';
type CategoryFilter = 'all' | Category;

// Export for use in experiment pages
export { experiments };
export type { Experiment };

function Lab() {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('newest');

  // Filter & sort
  const filtered = useMemo(() => {
    let list = [...experiments];

    if (categoryFilter !== 'all') {
      list = list.filter((e) => e.category === categoryFilter);
    }
    list.sort((a, b) => {
      const aDate = a.date === 'TBD' ? '0000.00' : a.date;
      const bDate = b.date === 'TBD' ? '0000.00' : b.date;
      const dateCmp = bDate.localeCompare(aDate);
      const tiebreak = dateCmp !== 0 ? dateCmp : b.number.localeCompare(a.number);
      return sortBy === 'newest' ? tiebreak : -tiebreak;
    });

    return list;
  }, [categoryFilter, sortBy]);

  return (
    <div className="lab">
      <div className="scanlines" />

      <header className="header">
        <div className="headerTop">
          <span className="logo">dkdk.lab</span>
          <span className="version">v0.1</span>
        </div>
        <div className="lastUpdate">last update. 2026.04.07. 00:00</div>
        <p className="headerDesc">
          Visual coding experiments.<br />
          Exploring time, motion, and retro aesthetics through code.
        </p>
        <div className="headerMeta">
          <span>total: {experiments.length}</span>
          <span>exp: {experiments.filter(e => e.category === 'exp').length}</span>
          <span>work: {experiments.filter(e => e.category === 'work').length}</span>
        </div>
      </header>

      {/* Filter & Sort Controls */}
      <div className="controls">
        <div className="controlRow">
          <div className="filterGroup">
            <span className="controlLabel">&gt; filter</span>
            <div className="filterButtons">
              {(['all', 'exp', 'work', 'archive'] as CategoryFilter[]).map((c) => (
                <button
                  key={c}
                  className={`filterBtn ${categoryFilter === c ? 'active' : ''} ${c !== 'all' ? c : ''}`}
                  onClick={() => setCategoryFilter(c)}
                >
                  {c === 'all' ? 'ALL' : categoryLabel[c]}
                </button>
              ))}
            </div>
          </div>
          <div className="sortGroup">
            <span className="controlLabel">&gt; sort</span>
            <div className="filterButtons">
              {([
                ['newest', 'NEWEST'],
                ['oldest', 'OLDEST'],
              ] as [SortKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className={`filterBtn ${sortBy === key ? 'active' : ''}`}
                  onClick={() => setSortBy(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="filterResult">
          &gt; showing {filtered.length} of {experiments.length}
        </div>
      </div>

      <main className="cardGrid">
        {filtered.length === 0 ? (
          <div className="emptyState">
            <span className="emptyIcon">∅</span>
            <p>No experiments match the current filter.</p>
          </div>
        ) : (
          filtered.map((exp) => {
            const isInternal = exp.url?.startsWith('/');
            const isExternal = exp.url && !isInternal;

            const cardContent = (
              <>
                <div className="cardThumbnail">
                  <div className="cardThumbnailInner">{exp.thumbnail}</div>
                </div>
                <div className="cardBody">
                  <div className="cardHeader">
                    <span className="cardNumber">#{exp.number}</span>
                    <span className={`cardCategory ${exp.category}`}>
                      {categoryLabel[exp.category]}
                    </span>
                  </div>
                  <h2 className="cardTitle">{exp.title}</h2>
                  <p className="cardDesc">{exp.description}</p>
                  <div className="cardTags">
                    {exp.tags.map((tag) => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="cardFooter">
                  <span className="cardDate">{exp.date}</span>
                  <span className="cardLink">
                    {exp.url ? 'View experiment →' : 'Coming soon'}
                  </span>
                </div>
              </>
            );

            if (isInternal) {
              return (
                <Link key={exp.id} to={exp.url!} className="card">
                  {cardContent}
                </Link>
              );
            }
            if (isExternal) {
              return (
                <a key={exp.id} href={exp.url} target="_blank" rel="noopener noreferrer" className="card">
                  {cardContent}
                </a>
              );
            }
            return (
              <div key={exp.id} className="card">
                {cardContent}
              </div>
            );
          })
        )}
      </main>

      <footer className="footer">
        <p className="footerText">
          dkdk.lab — <a href="https://dkdk.me">dkdk.me</a>
        </p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Lab />} />
        <Route path="/circle-reveal" element={<CircleReveal />} />
        <Route path="/velocity-cards" element={<VelocityCards />} />
        <Route path="/red-type" element={<RedType />} />
        <Route path="/gen-grid" element={<GenGrid />} />
        <Route path="/blind-reveal" element={<BlindReveal />} />
        <Route path="/low-poly-orbit" element={<LowPolyOrbit />} />
        <Route path="/text-repel" element={<TextRepel />} />
        <Route path="/liquid-type" element={<LiquidType />} />
        <Route path="/ink-drop" element={<InkDrop />} />
        <Route path="/news-tunnel" element={<NewsTunnel />} />
        <Route path="/voice-gravity" element={<VoiceGravity />} />
        <Route path="/melody-select" element={<MelodySelect />} />
        <Route path="/ascii-lens" element={<AsciiLens />} />
        <Route path="/branch-veins" element={<BranchVeins />} />
        <Route path="/dot-breath" element={<DotBreath />} />
        <Route path="/ink-sound" element={<InkSound />} />
        <Route path="/scroll-cinema" element={<ScrollCinema />} />
        <Route path="/fossil-dig" element={<FossilDig />} />
        <Route path="/breath-glass" element={<BreathGlass />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
